import React, { useState, useEffect } from 'react';
import { notification, Card, Row, Col, Modal, Form, Input, Button, Select, Upload, Tooltip } from 'antd';
import { useNavigate, useLocation } from "react-router-dom";
import { getNewsResources, addAdditionalServices, delAdditionalServices, markedFavouriteUnfavourite, getResourceOptions, getPresignedUrl } from '../../api/AdditionalServices';
import UserHeader from '../../components/header/UserHeader';
import UserDrawer from '../../components/drawer/UserDrawer';
import headerLogo from '../../assets/images/MyFriendNewsLogo.svg';
import goBackButton from '../../assets/images/GoBackButtonAdditionalServices.svg';
import "../additionalservices/AdditionalServices.css";
import addButton from '../../assets/images/AddIconAdditionalServices.svg';
import editButton from '../../assets/images/EditIconAdditionalServices.svg';
import deleteButton from '../../assets/images/DeleteIconAdditionalServices.svg';
import { QuestionCircleOutlined, UploadOutlined } from "@ant-design/icons";
import ClipLoader from "react-spinners/ClipLoader";
import { HeartOutlined, HeartFilled } from '@ant-design/icons';
import { getUserSubscriptionDetails } from '../../api/password';

const { Option } = Select;

const NewsResourcePage = () => {
    const [loading, setLoading] = useState(true);
    const [newsResources, setNewsResources] = useState([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [form] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const location = useLocation();
    const [selectedItem, setSelectedItem] = useState(null);
    const { backgroundColor, message, modalTitle, type } = location.state;
    const navigate = useNavigate();
    const [resourceOptions, setResourceOptions] = useState([]);
    const [nameOptions, setNameOptions] = useState([]);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [uploadedImageUrl, setUploadedImageUrl] = useState(null);

    const handleFileChange = async ({ file }) => {
        try {
            if (file) {    
                const fileType = file.type;    
                const response = await getPresignedUrl({ fileType: fileType });    
                const presignedUrl = response.uploadUrl;    
                const uploadResponse = await fetch(presignedUrl, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': fileType,
                    },
                    body: file,
                });
    
                if (uploadResponse.ok) {
                    const imageUrl = presignedUrl.split('?')[0]; 
                    setUploadedImageUrl(imageUrl);
                    setSelectedImageUrl('');
                } else {
                    console.error("Upload to S3 failed. Status:", uploadResponse.status);
                    throw new Error("Upload to S3 failed");
                }
            }
        } catch (error) {
            console.error("Error uploading file: ", error);
            notification.error({
                message: 'Upload Failed',
                description: error.message || 'An error occurred while uploading the file.',
                duration: 10
            });
        }
    };

    const fetchResourceOptions = async () => {
        try {
            const response = await getResourceOptions();
            const data = response.resources;

            const options = {
                news: data.News || [],
                game: data.Game || [],
                leisure: data.Leisure || []
            };
            Object.keys(options).forEach(key => {
                options[key] = options[key].map(resource => ({
                    ...resource,
                    imageUrl: resource.imageUrl || `https://picsum.photos/seed/${resource.name.toLowerCase().replace(/\s/g, '')}/300/200`
                }));
            });
            setResourceOptions(options);
        } catch (error) {
            console.error('', error);
        }
    };

    useEffect(() => {
        fetchResourceOptions();
    }, []);

    useEffect(() => {
        if (resourceOptions[type]) {
            setNameOptions(resourceOptions[type].map(resource => resource.name));
        }
    }, [resourceOptions, type]);

    const handleNameSearch = (input, option) => {
        return option.children.toLowerCase().includes(input.toLowerCase());
    };

    const handleNameChange = (value) => {
        const selectedResource = resourceOptions[type]?.find(resource => resource.name === value);

        if (selectedResource) {
            form.setFieldsValue({
                name: selectedResource.name,
                url: selectedResource.url,
            });
            setSelectedImageUrl(selectedResource.imageUrl);
            setUploadedImageUrl(null);
        }
    };

    const fetchNewsResources = async () => {
        try {
            setLoading(true);
            const response = await getNewsResources(type);
            if (response.data && response.data.length > 0) {
                setNewsResources(response.data.map(item => ({
                    ...item,
                    imageUrl: item.imageUrl || `https://picsum.photos/seed/${item.name.toLowerCase().replace(/\s/g, '')}/300/200`
                })));
            } else {
                setNewsResources([]);
                notification.info({
                    message: 'No News Preferences',
                    description: 'No news preferences found for this user.',
                    duration: 10
                });
            }
        } catch (error) {
            setNewsResources([]);
            notification.info({
                message: 'Information',
                description: `Please add your chosen Links!`,
                duration: 10
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNewsResources();
    }, [type]);

    const openDrawer = () => {
        setIsDrawerOpen(true);
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false);
    };

    const handleGoBackButton = () => {
        navigate("/landing-page");
    };

    const handleAddButton = () => {
        setNameOptions(prevOptions => Array.isArray(prevOptions) ? prevOptions : []);
        setIsModalOpen(true);
    };

    const handleEditButton = () => {
        setIsEditMode(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        form.resetFields();
    };

    const handleOk = async () => {
        try {
            await form.validateFields();
            const values = form.getFieldsValue();
            const { name, url } = values;
            let finalImageUrl = uploadedImageUrl || selectedImageUrl || '';
    
            const userSubscriptionDetails = await getUserSubscriptionDetails();
            const { planName } = userSubscriptionDetails;
    
            if (planName === 'Free Plan') {
                try {
                    const newsResourcesResponse = await getNewsResources(type);
                    const data = newsResourcesResponse.data || [];
    
                    if (data.length >= 1) {
                        notification.warning({
                            message: 'Upgrade Required',
                            description: "You are on the Free plan. You can't add more than one item. To add more, upgrade your subscription.",
                            duration: 10,
                        });
                        setIsModalOpen(false);
                        form.resetFields();
                        return;
                    }
                } catch (error) {
                    if (error.message) {
                        const additionalServiceData = { name, url, type, imageUrl: finalImageUrl };
                        await addAdditionalServices(additionalServiceData);
    
                        notification.success({
                            message: 'Success',
                            description: `Successfully added ${type} resource.`,
                            duration: 10,
                        });
    
                        setIsModalOpen(false);
                        form.resetFields();
                        setSelectedImageUrl('');
                        setUploadedImageUrl(null);
                        fetchNewsResources();
                        return;
                    } else {
                        throw error;
                    }
                }
            }

            const additionalServiceData = { name, url, type, imageUrl: finalImageUrl };
            await addAdditionalServices(additionalServiceData);
    
            notification.success({
                message: 'Success',
                description: `Successfully added ${type} resource.`,
                duration: 10,
            });
    
            setIsModalOpen(false);
            form.resetFields();
            setSelectedImageUrl('');
            setUploadedImageUrl(null);
            fetchNewsResources();
        } catch (error) {
            console.error('Error adding additional service:', error);
            notification.error({
                message: 'Error',
                description: error.message || 'Failed to add additional service.',
                duration: 10,
            });
            setIsModalOpen(false);
            form.resetFields();
        }
    };
    

    const handleDeleteClick = (item) => {
        setSelectedItem(item);
        setModalVisible(true);
    };

    const handleModalOk = async () => {
        try {
            if (selectedItem) {
                const data = {
                    type,
                    name: selectedItem.name,
                    url: selectedItem.url,
                };
                await delAdditionalServices(data);
                notification.success({
                    message: 'Success',
                    description: `Successfully deleted ${type} resource.`,
                    duration: 10
                });
                setIsEditMode(false);
                fetchNewsResources();
            }
        } catch (error) {
            console.error('Error deleting additional service:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to delete additional service.',
                duration: 10
            });
        } finally {
            setModalVisible(false);
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setIsEditMode(false);
        setSelectedItem(null);
    };

    const handleFavoriteToggle = async (item) => {
        try {
            const payload = {
                type: type,
                name: item.name,
                url: item.url,
                favourite: !item.favourite
            };

            await markedFavouriteUnfavourite(payload);

            setNewsResources(prevResources =>
                prevResources.map(resource =>
                    resource.name === item.name
                        ? { ...resource, favourite: !resource.favourite }
                        : resource
                )
            );

            notification.success({
                message: 'Success',
                description: `Successfully ${payload.favourite ? 'added to' : 'removed from'} favorites.`,
                duration: 10
            });
        } catch (error) {
            console.error('Error toggling favorite:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to update favorite status.',
                duration: 10
            });
        }
    };

    return (
        <div style={{ minHeight: "100dvh", overflow: "hidden", background: "#fefcfe" }}>
            <Row justify={{ xs: "space-around", md: "space-around", lg: "space-between" }} style={{ marginBottom: 20 }}>
                <Col xs={{ span: 22 }} lg={{ span: 24 }} md={{ span: 22 }}>
                    <UserHeader setDrawer={openDrawer} backgroundColor={backgroundColor} headerLogo={headerLogo} />
                </Col>
                {isDrawerOpen && (
                    <Col span={4}>
                        <UserDrawer isDrawerOpen={isDrawerOpen} onClose={closeDrawer} />
                    </Col>
                )}
            </Row>
            <Row justify="space-between" align="middle" className="go-back-button-home">
                <Col>
                    <button onClick={handleGoBackButton} className="button-styling-additonalservices">
                        <img src={goBackButton} style={{ paddingTop: '4px' }} alt="Go Back Button" />
                    </button>
                </Col>
                <Col className="col-center">
                    <span className="go-back-message">{message}</span>
                </Col>
                <Col className="add-styling-additonalservices">
                    <button onClick={handleAddButton} className="button-styling-additonalservices">
                        <img src={addButton} alt="Add Button" />
                    </button>
                </Col>
                <Col>
                    <button onClick={handleEditButton} className="button-styling-additonalservices">
                        <img src={editButton} alt="Edit Button" />
                    </button>
                </Col>
            </Row>
            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                    <ClipLoader loading={loading} size={50} aria-label="Loading Spinner" data-testid="loader" />
                </div>) : (
                <Row gutter={[16, 16]} style={{ padding: '50px' }}>
                    {newsResources.map((item, index) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={index}>
                            <Card
                                style={{
                                    borderBottomLeftRadius: '20px',
                                    borderBottomRightRadius: '20px',
                                    position: 'relative',
                                    minHeight: '320px',
                                }}
                                hoverable
                                cover={
                                    <div style={{ height: '150px', overflow: 'hidden', position: 'relative', borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
                                        <img
                                            alt="news cover"
                                            src={item.imageUrl}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                        {isEditMode && (
                                            <button
                                                onClick={() => handleDeleteClick(item)}
                                                style={{
                                                    position: 'absolute',
                                                    top: '-10px',
                                                    left: '14px',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    zIndex: 1,
                                                    display: 'flex',
                                                    justifyContent: "flex-end",
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                <img src={deleteButton} alt="Delete Button" style={{ width: '40px', height: '40px' }} />
                                            </button>
                                        )}
                                    </div>

                                }
                                actions={[
                                ]}
                            >
                                <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                    <div style={{ minHeight: '0px' }}>
                                        <Card.Meta
                                            title={
                                                <>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                        <span style={{ textAlign: 'center', fontWeight: 'bold', flexGrow: 1 }}>
                                                            {item.name}
                                                        </span>
                                                        <span
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleFavoriteToggle(item);
                                                            }}
                                                        >
                                                            {item.favourite ? <HeartFilled style={{ color: 'red' }} /> : <HeartOutlined />}
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#1D4D7A' }}>
                                                        {item.metadata?.title}
                                                    </div>
                                                </>
                                            }
                                            description={
                                                <div>
                                                    {item.metadata?.description}
                                                </div>
                                            }
                                        />
                                    </div>
                                </a>

                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            <Modal
                title={`Add ${modalTitle} Resource`}
                open={isModalOpen}
                closable={false}
                onCancel={handleCancel}
                footer={[]}
            >
                <Form form={form} layout="vertical" name="newsResourceForm">
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please input the name!' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Select or enter a name"
                            onChange={handleNameChange}
                            onSearch={(input) => {
                                if (input && !nameOptions.includes(input)) {
                                    setNameOptions(prevOptions => [...prevOptions, input]);
                                }
                            }}
                            filterOption={handleNameSearch}
                            className='dropdown-styling-modal'
                        >
                            {Array.isArray(nameOptions) && nameOptions.map((option) => (
                                <Option key={option} value={option}>{option}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="url"
                        label="URL"
                        rules={[
                            {
                                required: true,
                                message: 'Please input the URL!'
                            },
                            {
                                type: 'url',
                                message: 'Please enter a valid URL!',
                            }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={
                            <span>
                              Image&nbsp;
                              <Tooltip title="You can upload your own image or otherwise it is selected from resource library.">
                                <QuestionCircleOutlined />
                              </Tooltip>
                            </span>
                          }
                        name="image"
                    >
                        <Upload
                            listType="picture"
                            beforeUpload={() => false} 
                            onChange={handleFileChange}
                        >
                            <Button icon={<UploadOutlined />}>Upload Image (Optional)</Button>
                        </Upload>
                    </Form.Item>
                    <Button key="cancel" onClick={handleCancel} style={{ backgroundColor: backgroundColor, borderColor: backgroundColor, color: 'white' }}>
                        Cancel
                    </Button>
                    <Button
                        key="submit"
                        type="primary"
                        style={{ backgroundColor: backgroundColor, borderColor: backgroundColor, marginRight: '10px', marginTop: '10px' }}
                        onClick={handleOk}
                    >
                        Submit
                    </Button>
                </Form>
            </Modal>
            <Modal
                title="Confirm Deletion"
                visible={modalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="Delete"
                okButtonProps={{ danger: true }}
                bodyStyle={{ padding: '16px' }}
                closable={false}
                footer={[
                    <Button
                        key="ok"
                        type="primary"
                        onClick={handleModalOk}
                        className='delete-button-additional-service'
                    >
                        Delete
                    </Button>,

                    <Button
                        key="cancel"
                        onClick={handleModalCancel}
                        className='cancel-button-additional-service'
                    >
                        No
                    </Button>
                ]}
            >
                <div className='modal-text'>
                    <p>Are you sure you want to delete this resource?</p>
                </div>

            </Modal>
        </div>
    );
};

export default NewsResourcePage;